/**
 * Stable machine-readable refusal codes (SDAD Sections 10, 65).
 *
 * Every client — including a Flutter scanner that may be several releases behind
 * the cloud on event day — branches on `code`, never on the human-readable
 * message. Codes are additive. An existing code's meaning is never repurposed,
 * because an old client would then act on a new meaning it has never seen.
 */
export const ERROR_CODES = {
    VALIDATION_FAILED: 400,
    AUTH_REQUIRED: 401,
    AUTH_INVALID_CREDENTIALS: 401,
    AUTH_TOKEN_INVALID: 401,
    AUTH_SESSION_REVOKED: 401,
    AUTH_SESSION_EXPIRED: 401,
    AUTH_RATE_LIMITED: 429,
    /**
     * Volume pressure on a route that has nothing to do with signing in.
     *
     * Distinct from AUTH_RATE_LIMITED because they send an operator to different
     * places: one is credential stuffing, the other is a busy public sale.
     */
    REQUEST_RATE_LIMITED: 429,
    /** Credentials were correct; the second factor has not been presented yet. */
    AUTH_MFA_REQUIRED: 401,
    AUTH_MFA_INVALID: 401,
    /** This deployment was given no CABS Identity issuer, so the path is not offered. */
    AUTH_CABS_NOT_CONFIGURED: 503,
    /** The sign-in request itself was malformed — a return target we do not serve. */
    AUTH_CABS_REQUEST_INVALID: 400,
    /**
     * The sign-in did not complete. Deliberately one code for every way that can
     * happen: which check failed is recorded server-side, and telling the browser
     * whether a state was unknown, expired or already spent is telling an attacker
     * which of those it was.
     */
    AUTH_CABS_SIGN_IN_FAILED: 401,
    /**
     * Authenticated at CABS, bound to no GateCheckPro account.
     *
     * Separate from a failed sign-in because it is not a failure and the remedy is
     * different: nothing went wrong, this identity simply has no account here yet,
     * and the person is sent to the binding ceremony rather than back to sign in.
     */
    AUTH_CABS_SUBJECT_NOT_BOUND: 403,
    /**
     * A binding ceremony for this account is already open.
     *
     * A conflict rather than a failure: the remedy is to finish the one in flight
     * or let it expire, not to try again immediately.
     */
    AUTH_CABS_CEREMONY_IN_PROGRESS: 409,
    /** The link did not complete. One code for every way it can fail benignly. */
    AUTH_CABS_MIGRATION_FAILED: 400,
    /**
     * This GateCheckPro account is already linked to a different CABS identity.
     *
     * Separate from the next one because the remedies are opposite: this person
     * should sign in with the identity already linked, or have the existing link
     * revoked. Collapsing the two would tell them to do the wrong one.
     */
    AUTH_CABS_ACCOUNT_ALREADY_BOUND: 409,
    /** This CABS identity is already linked to a different GateCheckPro account. */
    AUTH_CABS_SUBJECT_ALREADY_BOUND: 409,
    /**
     * First use, but those contact details already belong to an account.
     *
     * Its own code because it is the one outcome that must never become a silent
     * bind, and because the remedy is specific: sign in to the existing account
     * and link this identity through the two-proof ceremony.
     */
    AUTH_CABS_EXISTING_ACCOUNT_REQUIRES_MIGRATION: 409,
    /** First use with no verified contact fact to create an account from. */
    AUTH_CABS_CONTACT_NOT_VERIFIED: 422,
    /**
     * A back-channel logout token that did not validate.
     *
     * 400 rather than 401: the caller is CABS Identity, not a user agent, and the
     * OpenID back-channel logout specification asks a relying party to answer a
     * bad token with 400 so the sender can tell a rejected instruction from an
     * authentication problem of its own.
     */
    AUTH_CABS_LOGOUT_TOKEN_INVALID: 400,
    AUTH_MFA_ALREADY_ENROLLED: 409,
    AUTH_MFA_NOT_ENROLLED: 409,
    AUTHZ_CAPABILITY_DENIED: 403,
    AUTHZ_MEMBERSHIP_INACTIVE: 403,
    /**
     * The actor holds the capability but has not proved a second factor in a
     * session that a privileged capability requires (SDAD Section 12.2). The
     * remedy is to enrol or re-authenticate, not to be granted a different role.
     */
    AUTHZ_MFA_REQUIRED: 403,
    /** An actor may not strip their own last owning grant and orphan a tenant. */
    AUTHZ_SELF_DEMOTION_DENIED: 403,
    /**
     * A scanner device that has been revoked, or was never activated, tried to
     * act. Distinct from a user capability failure: the subject is a handset, and
     * the remedy is re-provisioning rather than a role change.
     */
    AUTHZ_SCANNER_NOT_ACTIVE: 403,
    /**
     * An active scanner submitted for a gate it is not assigned to.
     *
     * Not a wrong-gate *admission* question — that is a decision about a ticket.
     * This is a device claiming a post it was not given, and it is refused before
     * any ticket is touched.
     */
    AUTHZ_SCANNER_GATE_NOT_ASSIGNED: 403,
    RESOURCE_NOT_FOUND: 404,
    RESOURCE_CONFLICT: 409,
    /**
     * A write carried a precondition that no longer holds — the caller edited a
     * version of the resource that has since moved.
     *
     * Distinct from RESOURCE_CONFLICT, which says the resource is in the wrong
     * *state* for the command. This says the command was written against the
     * wrong *content*, and the remedy is to reload and decide, not to retry.
     * Silently merging would let one organizer overwrite another's work without
     * either of them seeing it happen.
     */
    VENUE_MAP_TOPOLOGY_STALE: 412,
    /**
     * A seating-configuration save carried a precondition that no longer holds.
     *
     * Its own code rather than a bare 412, so a client branches on the business
     * event instead of on a status that any future precondition could also
     * produce. Like the topology one, it is deliberately not a state-conflict
     * refusal reason: the remedy is reload and decide, not retry.
     */
    SEATING_CONFIGURATION_STALE: 412,
    EMAIL_ALREADY_REGISTERED: 409,
    PHONE_ALREADY_REGISTERED: 409,
    PHONE_INVALID: 400,
    PHONE_COUNTRY_AMBIGUOUS: 400,
    ORGANIZATION_SLUG_TAKEN: 409,
    MEMBERSHIP_ALREADY_EXISTS: 409,
    MEMBERSHIP_STATE_INVALID: 409,
    INVITATION_STATE_INVALID: 409,
    ORGANIZATION_VERIFICATION_STATE_INVALID: 409,
    /** Checkout and inventory (SDAD Sections 15.2, 18). */
    TICKETS_UNAVAILABLE: 409,
    QUANTITY_UNAVAILABLE: 409,
    CHECKOUT_EXPIRED: 409,
    EVENT_NOT_ON_SALE: 409,
    TICKET_PRODUCT_NOT_ON_SALE: 409,
    /**
     * DropControl (SDAD Section 19).
     *
     * 409 rather than 403: nothing is wrong with the caller's authorisation. The
     * sale has a waiting room and they have not come through it, which is a state
     * of the sale rather than a property of the person.
     */
    DROPCONTROL_ADMISSION_REQUIRED: 409,
    ORDER_STATE_INVALID: 409,
    /** Payment collection (SDAD Sections 19, 54). */
    /** No collection adapter is configured. Honest, and not a fault. */
    PAYMENT_PROVIDER_UNAVAILABLE: 503,
    /**
     * The customer asked to pay through a rail this deployment does not offer.
     *
     * Distinct from PAYMENT_PROVIDER_UNAVAILABLE: a provider is connected and
     * some rails work, just not this one.
     */
    PAYMENT_RAIL_UNAVAILABLE: 409,
    /** The order is not in a state where money may be taken. */
    ORDER_NOT_PAYABLE: 409,
    /** The reservation lapsed before payment. Nothing was charged. */
    HOLD_EXPIRED: 409,
    /**
     * The order contains a security mode GateCheckPro cannot currently fulfil.
     *
     * Refused *before any external money movement*. Selling Secure Pass while
     * Secure Pass does not exist would take money for an assurance the platform
     * cannot deliver, and a refund afterwards is not a substitute for not
     * charging (SDAD Section 54).
     */
    SECURITY_MODE_NOT_FULFILLABLE: 409,
    /** The webhook signature did not verify, or none was presented. */
    WEBHOOK_SIGNATURE_INVALID: 401,
    /** Payment is verified; the tickets are not written yet. */
    TICKETS_NOT_ISSUED_YET: 409,
    /**
     * The refund cannot be made as asked.
     *
     * One code for every refusal a refund can produce, with the specific reason
     * in `reasonCode` — `REFUND_EXCEEDS_COLLECTION`, `TICKET_ALREADY_REFUNDED`,
     * `ORDER_WAS_FREE` and the rest. A surface switches on the reason; the status
     * is 409 in every case because all of them mean "the world is not in a state
     * where this refund makes sense", never "you sent malformed JSON".
     */
    REFUND_REFUSED: 409,
    IDEMPOTENCY_KEY_REQUIRED: 400,
    IDEMPOTENCY_KEY_REUSED: 409,
    IDEMPOTENCY_REQUEST_IN_FLIGHT: 409,
    INTERNAL_ERROR: 500,
    SERVICE_UNAVAILABLE: 503,
};
export function isApiErrorBody(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const candidate = value.error;
    return (typeof candidate === 'object' &&
        candidate !== null &&
        typeof candidate.code === 'string');
}
//# sourceMappingURL=error-codes.js.map